/**
 * Slide-in edit panel for a single spending phase — opened from a row click
 * in SpendingPhasesTable (#1538). Mirrors HoldingEditDrawer's conventions
 * (same form instance, save-on-blur, two-click delete) with three additions
 * specific to spending phases: per-category itemization (confirm-gated,
 * one-way once used), a live phase total pinned to the top of the drawer,
 * and "copy spending from another phase" (gated behind an overwrite warning
 * when the target phase already has data).
 *
 * Mounted with `key={phase.phaseId}` by the parent so switching to a
 * different phase remounts this component fresh — every hook here
 * (including the three per-category useFieldArrays) can then assume a
 * stable phaseIndex for its whole mounted lifetime.
 */

import { useEffect, useRef, useState } from 'react';
import { type Control, type UseFormRegister, type UseFormSetValue, type UseFormGetValues, type FieldErrors } from 'react-hook-form';
import { SpendingPhase, ExpenseLineItem, type Step6FormData } from '../../types/wizard';
import { planYearToCalendar } from '../../utils/formatters';
import { CurrencyInput } from './Step3Holdings';
import {
  CATEGORY_ORDER,
  CATEGORY_META,
  SpendingCategoryKey,
  getCategoryItems,
  getCategoryTotal,
  getPhaseTotal,
  phaseHasSpendingData,
  looksLikeTabularPaste,
  parseClipboardLineItems,
  projectAmountToPhaseStart,
} from '../../utils/spendingPhaseHelpers';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

interface SpendingPhaseEditDrawerProps {
  isOpen: boolean;
  phaseIndex: number;
  phases: ReadonlyArray<SpendingPhase>;
  minPhases: number;
  maxPhases: number;
  control: Control<Step6FormData>;
  register: UseFormRegister<Step6FormData>;
  setValue: UseFormSetValue<Step6FormData>;
  getValues: UseFormGetValues<Step6FormData>;
  errors?: FieldErrors<Step6FormData>;
  planAsOfDate?: string;
  inflationAnnualPct: number;
  isDefaultInflationRate: boolean;
  onFieldBlur: () => void;
  onClose: () => void;
  onRemovePhase: () => void;
}

function SpendingPhaseEditDrawer({
  isOpen,
  phaseIndex,
  phases,
  minPhases,
  maxPhases,
  control,
  register,
  setValue,
  getValues,
  errors,
  planAsOfDate,
  inflationAnnualPct,
  isDefaultInflationRate,
  onFieldBlur,
  onClose,
  onRemovePhase,
}: Readonly<SpendingPhaseEditDrawerProps>) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [confirmDeletePhase, setConfirmDeletePhase] = useState(false);
  const confirmDeleteTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [confirmingBreakdownCat, setConfirmingBreakdownCat] = useState<SpendingCategoryKey | null>(null);
  const breakdownTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [collapsedCats, setCollapsedCats] = useState<Partial<Record<SpendingCategoryKey, boolean>>>({});
  const [copySourceId, setCopySourceId] = useState('');
  const [pendingCopySourceId, setPendingCopySourceId] = useState<string | null>(null);
  const [copyConfirmMessage, setCopyConfirmMessage] = useState<string | null>(null);
  const copyConfirmTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const prefix = `spendingPhases.${phaseIndex}` as const;

  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && drawerRef.current) {
        const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
          'input:not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const first = drawerRef.current?.querySelector<HTMLElement>('input:not(:disabled), select:not(:disabled)');
    first?.focus();
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (confirmDeleteTimerRef.current) clearTimeout(confirmDeleteTimerRef.current);
      if (breakdownTimerRef.current) clearTimeout(breakdownTimerRef.current);
      if (copyConfirmTimerRef.current) clearTimeout(copyConfirmTimerRef.current);
    };
  }, []);

  if (!isOpen) return null;

  const phase = phases[phaseIndex];
  if (!phase) return null;

  const isLast = phaseIndex === phases.length - 1;
  const phaseTotal = getPhaseTotal(phase);
  const phaseErrors = errors?.spendingPhases?.[phaseIndex];
  const otherPhases = phases.filter((_, i) => i !== phaseIndex);

  function syncFlatTotal(key: SpendingCategoryKey) {
    const meta = CATEGORY_META[key];
    const items = (getValues(`${prefix}.${meta.itemsField}`) as ExpenseLineItem[] | undefined) || [];
    const sum = items.reduce((s, i) => s + (Number(i.annualAmount) || 0), 0);
    setValue(`${prefix}.${key}`, sum, { shouldValidate: true });
  }

  function handleDeleteClick() {
    if (!confirmDeletePhase) {
      setConfirmDeletePhase(true);
      confirmDeleteTimerRef.current = setTimeout(() => setConfirmDeletePhase(false), 3000);
      return;
    }
    if (confirmDeleteTimerRef.current) clearTimeout(confirmDeleteTimerRef.current);
    onRemovePhase();
  }

  function getItems(key: SpendingCategoryKey): ExpenseLineItem[] {
    const meta = CATEGORY_META[key];
    return (getValues(`${prefix}.${meta.itemsField}`) as ExpenseLineItem[] | undefined) || [];
  }

  /** Plain getValues/setValue array mutation — deliberately NOT useFieldArray,
   * which coerces an undefined items field to [] the moment it's mounted
   * (even before the user opts into itemizing), silently itemizing every
   * category on drawer open. */
  function replaceItems(key: SpendingCategoryKey, items: ExpenseLineItem[] | undefined) {
    const meta = CATEGORY_META[key];
    setValue(`${prefix}.${meta.itemsField}`, items, { shouldValidate: true });
  }

  function handleBreakdownClick(key: SpendingCategoryKey) {
    if (confirmingBreakdownCat !== key) {
      setConfirmingBreakdownCat(key);
      breakdownTimerRef.current = setTimeout(() => setConfirmingBreakdownCat((prev) => (prev === key ? null : prev)), 3000);
      return;
    }
    if (breakdownTimerRef.current) clearTimeout(breakdownTimerRef.current);
    const seedAmount = Number(getValues(`${prefix}.${key}`)) || 0;
    replaceItems(key, seedAmount > 0 ? [{ itemId: crypto.randomUUID(), description: '', annualAmount: seedAmount }] : []);
    setConfirmingBreakdownCat(null);
    onFieldBlur();
  }

  function handleAddItem(key: SpendingCategoryKey) {
    replaceItems(key, [...getItems(key), { itemId: crypto.randomUUID(), description: '', annualAmount: 0 }]);
    onFieldBlur();
  }

  function handleRemoveItem(key: SpendingCategoryKey, itemIndex: number) {
    replaceItems(key, getItems(key).filter((_, i) => i !== itemIndex));
    syncFlatTotal(key);
    onFieldBlur();
  }

  /**
   * A single-cell paste (no tabs/newlines) is left alone — the browser's
   * default paste-into-text-field behavior handles it. A multi-cell paste
   * (copied from Excel/Sheets) is intercepted: the row pasted into is
   * replaced by the first parsed row, and any further rows are inserted
   * after it as new line items.
   */
  function handleDescriptionPaste(e: React.ClipboardEvent<HTMLInputElement>, key: SpendingCategoryKey, itemIndex: number) {
    const text = e.clipboardData.getData('text');
    if (!looksLikeTabularPaste(text)) return;
    e.preventDefault();
    const parsedRows = parseClipboardLineItems(text);
    if (parsedRows.length === 0) return;

    const current = getItems(key);
    const updated = [...current];
    updated[itemIndex] = { ...updated[itemIndex], description: parsedRows[0].description, annualAmount: parsedRows[0].annualAmount };
    const rest = parsedRows.slice(1).map((row) => ({ itemId: crypto.randomUUID(), description: row.description, annualAmount: row.annualAmount }));
    updated.splice(itemIndex + 1, 0, ...rest);

    replaceItems(key, updated);
    syncFlatTotal(key);
    onFieldBlur();
  }

  function performCopy(source: SpendingPhase) {
    CATEGORY_ORDER.forEach((key) => {
      const items = getCategoryItems(source, key);
      setValue(`${prefix}.${key}`, getCategoryTotal(source, key), { shouldValidate: true });
      replaceItems(key, items ? items.map((i) => ({ ...i, itemId: crypto.randomUUID() })) : undefined);
    });
    setCopyConfirmMessage(`Copied spending from "${source.label}"`);
    setCopySourceId('');
    setPendingCopySourceId(null);
    onFieldBlur();
    if (copyConfirmTimerRef.current) clearTimeout(copyConfirmTimerRef.current);
    copyConfirmTimerRef.current = setTimeout(() => setCopyConfirmMessage(null), 2500);
  }

  function handleCopyCommit() {
    const source = phases.find((p) => p.phaseId === copySourceId);
    if (!source) return;
    if (phaseHasSpendingData(phase)) {
      setPendingCopySourceId(copySourceId);
    } else {
      performCopy(source);
    }
  }

  function renderCategorySection(key: SpendingCategoryKey) {
    const meta = CATEGORY_META[key];
    const items = getCategoryItems(phase, key);
    const isItemized = items != null;
    const total = getCategoryTotal(phase, key);
    const collapsed = !!collapsedCats[key];
    const errorMessage = phaseErrors?.[key]?.message;

    return (
      <div key={key} className="spending-drawer-section">
        <button
          type="button"
          className="spending-cat-header"
          aria-expanded={!collapsed}
          onClick={() => setCollapsedCats((prev) => ({ ...prev, [key]: !prev[key] }))}
        >
          <div>
            <span className="spending-cat-name">{meta.label}</span>
            <span className="spending-cat-hint">{meta.hint}</span>
          </div>
          <span className="spending-cat-total-wrap">
            <span className="spending-cat-total">{formatCurrency(total)}</span>
            <span className="spending-cat-chevron">{collapsed ? '›' : '⌄'}</span>
          </span>
        </button>

        {!collapsed && (isItemized ? (
          <>
            <div className="spending-items-list">
              {(items || []).map((item, itemIndex) => (
                <div className="spending-item-row" key={item.itemId}>
                  <input
                    type="text"
                    className="form-input form-input-sm spending-item-desc"
                    placeholder="e.g. Council tax"
                    {...register(`${prefix}.${meta.itemsField}.${itemIndex}.description`)}
                    onBlur={onFieldBlur}
                    onPaste={(e) => handleDescriptionPaste(e, key, itemIndex)}
                  />
                  <span className="spending-item-amt">
                    <CurrencyInput
                      name={`${prefix}.${meta.itemsField}.${itemIndex}.annualAmount`}
                      control={control}
                      placeholder="£/yr"
                      onBlur={onFieldBlur}
                      onValueChange={() => syncFlatTotal(key)}
                    />
                  </span>
                  <button
                    type="button"
                    className="spending-item-remove"
                    aria-label="Remove line item"
                    onClick={() => handleRemoveItem(key, itemIndex)}
                  >
                    &#128465;
                  </button>
                </div>
              ))}
            </div>
            <button type="button" className="spending-add-item-btn" onClick={() => handleAddItem(key)}>
              + Add line item
            </button>
            <p className="spending-paste-hint">Tip: copy two columns (description, amount) from Excel or Sheets and paste into a description field above.</p>
            <div className="spending-items-sum">= {formatCurrency(total)} / year</div>
            <p className="spending-mandatory-note">Itemized &mdash; this category now always sums from its line items.</p>
          </>
        ) : (
          <>
            <div className="drawer-field">
              <CurrencyInput
                id={`${prefix}.${key}`}
                name={`${prefix}.${key}`}
                control={control}
                aria-invalid={errorMessage ? true : undefined}
                onBlur={onFieldBlur}
              />
              {errorMessage && <span className="error-message">{errorMessage}</span>}
            </div>
            <button
              type="button"
              className={`btn-link spending-breakdown-btn ${confirmingBreakdownCat === key ? 'confirming' : ''}`}
              onClick={() => handleBreakdownClick(key)}
            >
              {confirmingBreakdownCat === key ? "Click again — can't be undone" : 'Break this down into line items →'}
            </button>
          </>
        ))}
      </div>
    );
  }

  return (
    <>
      <button type="button" className="drawer-backdrop" aria-label="Close overlay" onClick={onClose} />
      <div ref={drawerRef} className="spending-phase-drawer" role="dialog" aria-modal="true" aria-label="Edit spending phase">
        <div className="spending-drawer-sticky-top">
          <div className="drawer-header">
            <div>
              <h3>{phase.label || 'Untitled phase'}</h3>
              <p className="drawer-sub">Phase {phaseIndex + 1} of {phases.length}</p>
            </div>
            <button type="button" className="drawer-close" onClick={onClose} aria-label="Close">&#10005;</button>
          </div>
          <p className="spending-drawer-total">Total this phase: <b>{formatCurrency(phaseTotal)}</b> / yr</p>
        </div>

        <div className="drawer-field">
          <label htmlFor={`${prefix}.label`}>Phase name</label>
          <input
            id={`${prefix}.label`}
            type="text"
            className={`form-input ${phaseErrors?.label ? 'error' : ''}`}
            {...register(`${prefix}.label`)}
            onBlur={onFieldBlur}
          />
          {phaseErrors?.label && <span className="error-message">{phaseErrors.label.message}</span>}
        </div>

        <div className="drawer-row">
          <div className="drawer-field">
            <label htmlFor={`${prefix}.startYear`}>Start Year</label>
            <input
              id={`${prefix}.startYear`}
              type="number"
              min="1"
              className={`form-input ${phaseErrors?.startYear ? 'error' : ''}`}
              {...register(`${prefix}.startYear`, { valueAsNumber: true })}
              onBlur={onFieldBlur}
              disabled={phaseIndex > 0}
            />
            {planAsOfDate && phase.startYear && (
              <span className="field-context-hint">{planYearToCalendar(phase.startYear, planAsOfDate)}</span>
            )}
            {phaseErrors?.startYear && <span className="error-message">{phaseErrors.startYear.message}</span>}
          </div>
          <div className="drawer-field">
            <label htmlFor={`${prefix}.endYear`}>End Year</label>
            <input
              id={`${prefix}.endYear`}
              type="number"
              min="1"
              className={`form-input ${phaseErrors?.endYear ? 'error' : ''}`}
              {...register(`${prefix}.endYear`, {
                valueAsNumber: true,
                onChange: (e: { target: { value: string } }) => {
                  if (!isLast) {
                    const newEnd = Number(e.target.value);
                    if (Number.isFinite(newEnd)) {
                      setValue(`spendingPhases.${phaseIndex + 1}.startYear`, newEnd + 1, { shouldValidate: true });
                    }
                  }
                },
              })}
              onBlur={onFieldBlur}
              placeholder={isLast ? 'Until end of projection' : undefined}
            />
            {isLast && !Number.isFinite(phase.endYear) && (
              <span className="field-context-hint">Continues through your full projection period</span>
            )}
            {planAsOfDate && Number.isFinite(phase.endYear) && (
              <span className="field-context-hint">{planYearToCalendar(phase.endYear!, planAsOfDate)}</span>
            )}
            {phaseErrors?.endYear && <span className="error-message">{phaseErrors.endYear.message}</span>}
          </div>
        </div>

        <div className="drawer-field">
          <label>Inflation</label>
          <div className="inflation-segmented" role="group" aria-label="Inflation adjustment">
            <button
              type="button"
              className={`inflation-seg-btn ${phase.inflationLinked === false ? 'inflation-seg-btn--fixed-active' : 'inflation-seg-btn--inactive'}`}
              onClick={() => { setValue(`${prefix}.inflationLinked`, false, { shouldValidate: true }); onFieldBlur(); }}
              aria-pressed={phase.inflationLinked === false}
            >Fixed</button>
            <button
              type="button"
              className={`inflation-seg-btn ${phase.inflationLinked !== false ? 'inflation-seg-btn--inflation-active' : 'inflation-seg-btn--inactive'}`}
              onClick={() => { setValue(`${prefix}.inflationLinked`, true, { shouldValidate: true }); onFieldBlur(); }}
              aria-pressed={phase.inflationLinked !== false}
            >Inflation adjusted</button>
          </div>
          <p className="field-hint">Fixed stays the same in cash terms; inflation-adjusted rises with prices each year.</p>
          {phase.inflationLinked !== false && phase.startYear > 1 && (
            <p className="field-hint inflation-preview">
              This phase doesn&apos;t start until Year {phase.startYear}
              {planAsOfDate ? ` (${planYearToCalendar(phase.startYear, planAsOfDate)})` : ''} &mdash; by
              then, today&apos;s {formatCurrency(phaseTotal)}/yr will already have grown to about{' '}
              <b>{formatCurrency(projectAmountToPhaseStart(phaseTotal, inflationAnnualPct, phase.startYear))}</b>/yr
              in inflated terms, assuming {isDefaultInflationRate
                ? `the default ${inflationAnnualPct}% inflation (you'll set your own on the Assumptions step)`
                : `your ${inflationAnnualPct}% inflation assumption`}.
            </p>
          )}
        </div>

        {otherPhases.length > 0 && (
          <div className="drawer-field">
            <label htmlFor="spending-copy-from-select">Copy spending from</label>
            {pendingCopySourceId ? (
              <div className="copy-warning">
                <p>
                  This phase already has spending entered. Copying from{' '}
                  <b>{phases.find((p) => p.phaseId === pendingCopySourceId)?.label}</b> will replace all three categories here.
                </p>
                <div className="copy-warning-actions">
                  <button type="button" className="btn-link" onClick={() => setPendingCopySourceId(null)}>Cancel</button>
                  <button
                    type="button"
                    className="btn-replace"
                    onClick={() => {
                      const source = phases.find((p) => p.phaseId === pendingCopySourceId);
                      if (source) performCopy(source);
                    }}
                  >
                    Replace data
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="copy-from-row">
                  <select
                    id="spending-copy-from-select"
                    className="form-input"
                    value={copySourceId}
                    onChange={(e) => setCopySourceId(e.target.value)}
                  >
                    <option value="">Choose a phase&hellip;</option>
                    {otherPhases.map((p) => (
                      <option key={p.phaseId} value={p.phaseId}>{p.label || 'Untitled phase'}</option>
                    ))}
                  </select>
                  <button type="button" className="btn-copy-commit" disabled={!copySourceId} onClick={handleCopyCommit}>
                    Copy
                  </button>
                </div>
                {copyConfirmMessage && <div className="copy-confirm visible">{copyConfirmMessage}</div>}
              </>
            )}
          </div>
        )}

        {CATEGORY_ORDER.map((key) => renderCategorySection(key))}

        <div className="drawer-delete">
          <button
            type="button"
            className={`btn-delete-text ${confirmDeletePhase ? 'confirming' : ''}`}
            onClick={handleDeleteClick}
            disabled={phases.length <= minPhases}
          >
            {confirmDeletePhase ? 'Click again to confirm' : 'Remove this phase'}
          </button>
          {confirmDeletePhase && !isLast && phases[phaseIndex + 1] && (
            <span className="drawer-min-note drawer-delete-consequence">
              {Number.isFinite(phase.endYear)
                ? `Years ${phase.startYear}–${phase.endYear} will be covered by "${phases[phaseIndex + 1].label}" instead`
                : `Its years will be covered by "${phases[phaseIndex + 1].label}" instead`}
            </span>
          )}
          {confirmDeletePhase && isLast && phaseIndex > 0 && (
            <span className="drawer-min-note drawer-delete-consequence">
              {`"${phases[phaseIndex - 1].label}" will continue to the end of your projection`}
            </span>
          )}
          {phases.length <= minPhases && <span className="drawer-min-note">Minimum {minPhases} phase{minPhases === 1 ? '' : 's'}</span>}
          {phases.length >= maxPhases && <span className="drawer-min-note">Maximum {maxPhases} phases</span>}
        </div>
      </div>
    </>
  );
}

export default SpendingPhaseEditDrawer;
